<template>
    <!-- Visible error state when the plan list didn't arrive from the server.
         No silent fallback to hardcoded data - if we can't show real pricing,
         we say so plainly and link to a retry. -->
    <div v-if="!plan" class="mkt-pricing-card plan-card plan-card--error">
        <q-icon name="cloud_off" size="32px" color="negative" class="q-mb-sm" />
        <div class="text-weight-bold q-mb-xs" style="font-size:1rem;color:#0a0f1a;">
            Pricing temporarily unavailable
        </div>
        <div class="text-grey-7 q-mb-md" style="font-size:0.85rem;line-height:1.5;">
            We couldn't load the <b>{{ tierCode }}</b> plan just now. Refresh the page, or
            <a href="/Home/Support" class="text-primary">contact support</a> if this persists.
        </div>
        <q-btn flat no-caps size="sm" label="Refresh" icon="refresh" @click="reloadPage" />
    </div>

    <div v-else class="mkt-pricing-card plan-card"
         :class="{
             'mkt-pricing-card--featured': featured,
             'plan-card--compact': compact
         }"
         :style="borderStyle">
        <div v-if="featured && featuredLabel" class="mkt-pricing-badge" :style="badgeStyle">{{ featuredLabel }}</div>

        <!-- Header: optional icon + name + tagline -->
        <div v-if="icon" class="row items-center q-mb-xs">
            <q-icon :name="icon" :style="`color:${accentColor};font-size:24px;`" class="q-mr-sm" />
            <span class="text-weight-bold" style="font-size:1.15rem;color:#0a0f1a;">{{ plan.name }}</span>
        </div>
        <div v-else class="text-weight-bold q-mb-xs" style="font-size:1.15rem;color:#0a0f1a;">
            {{ plan.name }}
        </div>

        <div v-if="plan.description" class="text-grey-6 q-mb-md" style="font-size:0.85rem;">
            {{ plan.description }}
        </div>

        <!-- Price -->
        <div class="q-mb-sm">
            <span class="text-weight-bold" :style="priceStyle">{{ priceDisplay }}</span>
            <span v-if="priceUnit" class="text-grey-6" style="font-size:0.85rem;">&nbsp;{{ priceUnit }}</span>
        </div>
        <div v-if="priceSubline" class="text-grey-6 q-mb-lg" style="font-size:0.8rem;">{{ priceSubline }}</div>

        <!-- CTA -->
        <q-btn v-if="!hideCta"
               :color="computedCtaColor"
               :label="ctaLabel || defaultCtaLabel"
               :href="computedCtaHref"
               no-caps unelevated
               class="full-width q-mb-lg"
               style="border-radius:8px;"
               :outline="ctaOutline" />

        <!-- Features list -->
        <div v-if="!compact" class="text-left">
            <div v-if="includesLabel" class="text-weight-bold q-mb-sm" style="font-size:0.8rem;color:#0a0f1a;">
                {{ includesLabel }}
            </div>
            <div v-for="(feature, idx) in resolvedFeatures" :key="idx" class="row no-wrap items-start q-mb-xs">
                <q-icon name="check" color="positive" size="16px" class="q-mr-xs q-mt-xs" style="flex-shrink:0;" />
                <span class="col" style="font-size:0.88rem;color:#555;line-height:1.5;" v-html="feature"></span>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * PlanCard - reusable pricing card bound to a single Sign_SubscriptionPlans row.
 *
 * Data source: looked up by `tierCode` from `getPlans()` in `planData.js`,
 * which prefers `window._pageConfig.plans` (server-rendered from DB) and
 * falls back to a bundled static snapshot if that's empty.
 *
 * Slotless by design - customization is via props so the component can be
 * dropped into any page without slot boilerplate. For plan-by-plan copy
 * overrides use the `features` prop (array of HTML strings). If not supplied,
 * features are auto-derived from the plan's flags.
 */
import { getPlanByTierCode, formatPriceDisplay, formatUserSubline } from './planData.js';

export default {
    name: 'PlanCard',
    props: {
        /** Required. Matches Sign_SubscriptionPlans.TierCode ('free', 'starter', 'collect', 'developer', 'pro', 'business'). */
        tierCode: { type: String, required: true },
        /** Toggle between monthly and annual price display. */
        annual: { type: Boolean, default: false },
        /** Adds the "Most Popular" featured styling / badge. */
        featured: { type: Boolean, default: false },
        /** Custom featured-badge label (defaults to "Most Popular"). */
        featuredLabel: { type: String, default: 'Most Popular' },
        /** Compact variant: hides feature list, smaller price. Useful for Index.vue teaser rows. */
        compact: { type: Boolean, default: false },
        /** Optional Material icon shown left of the plan name. */
        icon: { type: String, default: null },
        /** Override default features - pass an array of strings (HTML allowed). */
        features: { type: Array, default: null },
        /** Header above the features list. Defaults to 'Includes:' or omitted depending on context. */
        includesLabel: { type: String, default: 'Includes:' },
        /** Call-to-action button label. */
        ctaLabel: { type: String, default: null },
        /** Override the CTA href. Defaults to /Account/Register?plan=<tierCode>. */
        ctaHref: { type: String, default: null },
        /** CTA button color (Quasar color name). Defaults based on tier. */
        ctaColor: { type: String, default: null },
        /** CTA uses outline style. */
        ctaOutline: { type: Boolean, default: false },
        /** Hide the CTA button entirely (useful when parent supplies its own CTA). */
        hideCta: { type: Boolean, default: false },
        /** Show a border accent color matching the tier (used for "specialized" cards). */
        accented: { type: Boolean, default: false }
    },
    computed: {
        plan() {
            // Returns null if _pageConfig.plans wasn't populated (Platform.API
            // unreachable AND no cache) OR if the requested tierCode isn't in
            // the live list. The template renders a visible error block instead
            // of manufacturing synthetic plan data.
            const p = getPlanByTierCode(this.tierCode);
            if (!p) {
                console.warn(`[PlanCard] No plan found for tierCode="${this.tierCode}". ` +
                    `Check that Sign_SubscriptionPlans has a row for this tier and that ` +
                    `Platform.API is reachable from Platform.eSignPortal.`);
            }
            return p;
        },
        priceDisplay() {
            return formatPriceDisplay(this.plan, { annual: this.annual });
        },
        priceUnit() {
            if (!this.plan || this.plan.monthlyPrice === 0) return '';
            return '/ mo';
        },
        priceSubline() {
            if (!this.plan) return '';
            // Free tier headline: how many free signings, and the price per additional.
            // No-credit-card language reinforces the "truly free" positioning.
            if (this.plan.tierCode === 'free') {
                const freeCount = this.plan.packageLimit || 0;
                const extra = this.plan.overagePerPackage?.toFixed(2) ?? '0.00';
                if (freeCount > 0) {
                    return `${freeCount} free per month · $${extra} each after · no credit card`;
                }
                return `$${extra} per signing · no credit card`;
            }
            return formatUserSubline(this.plan, { annual: this.annual });
        },
        accentColor() {
            // Map the DB-stored color name to a usable CSS color. Quasar color names
            // (amber-9, teal, etc.) don't translate directly to CSS - use a lookup.
            const mapping = {
                grey: '#64748B',
                blue: '#2563EB',
                green: '#10B981',
                purple: '#8B5CF6',
                teal: '#0D9488',
                'amber-9': '#F59E0B'
            };
            return mapping[this.plan?.displayColor] || '#6366F1';
        },
        borderStyle() {
            if (!this.accented) return {};
            return { border: `2px solid ${this.accentColor}20`, borderColor: `${this.accentColor}40` };
        },
        badgeStyle() {
            if (!this.featured) return {};
            return { background: this.accentColor };
        },
        priceStyle() {
            return this.compact
                ? 'font-size:1.8rem;color:#0a0f1a;'
                : 'font-size:2.4rem;color:#0a0f1a;';
        },
        defaultCtaLabel() {
            // Honest copy: there is no "free trial" that unlocks paid features.
            // New accounts land on Free tier; upgrading is a separate, voluntary
            // step in the dashboard. No trial period, no auto-charge surprise.
            return this.plan?.tierCode === 'free' ? 'Start Free' : 'Get Started Free';
        },
        computedCtaHref() {
            if (this.ctaHref) return this.ctaHref;
            const code = this.plan?.tierCode || this.tierCode;
            return `/Account/Register?plan=${encodeURIComponent(code)}`;
        },
        computedCtaColor() {
            if (this.ctaColor) return this.ctaColor;
            // Default CTA color matches the tier accent.
            const mapping = {
                grey: 'primary',
                blue: 'primary',
                green: 'positive',
                purple: 'deep-purple',
                teal: 'primary',
                'amber-9': 'amber-9'
            };
            return mapping[this.plan?.displayColor] || 'primary';
        },
        resolvedFeatures() {
            // Caller-supplied feature list takes precedence. Otherwise auto-derive.
            if (Array.isArray(this.features) && this.features.length > 0) {
                return this.features;
            }
            return this.autoDerivedFeatures;
        },
        autoDerivedFeatures() {
            const p = this.plan;
            if (!p) return [];
            const out = [];

            // Signings allowance
            if (p.packageLimit === 0) {
                out.push(`Pay-as-you-go: $${p.overagePerPackage.toFixed(2)} per signing`);
            } else {
                let line = `${p.packageLimit.toLocaleString()} signings per month`;
                if (p.apiPackageLimit > 0) {
                    line += ` + ${p.apiPackageLimit.toLocaleString()} API signings`;
                }
                out.push(line);
            }

            // Templates + users
            out.push(`${p.templateLimit.toLocaleString()} templates`);
            if (p.userLimit === 1) {
                out.push('1 user seat');
            } else if (p.userLimit === -1) {
                out.push(`${p.baseUserCount} users included · unlimited scale`);
            } else {
                out.push(`${p.baseUserCount} users included (up to ${p.userLimit})`);
            }

            // Capabilities
            if (p.allowEmbeddedSigning) out.push('Embedded signing');
            if (p.allowApiAccess) out.push('REST API + webhooks');
            else if (p.allowWebhooks) out.push('Webhooks');
            if (p.allowBulkSend) out.push('Bulk send & signing groups');
            if (p.allowBranding) out.push('Custom branding');
            if (p.allowPaymentCollection) {
                const pct = (p.platformFeePercent * 100).toFixed(p.platformFeePercent < 0.01 ? 2 : 1);
                out.push(`Payment collection (${pct}% platform fee)`);
            }
            if (p.allowSsoScim) out.push('SSO / SCIM (Okta, Entra ID, etc.)');

            // Security-credit allowances
            const freeSec = [];
            if (p.freeIdvPerMonth > 0) freeSec.push(`${p.freeIdvPerMonth} free IDV`);
            if (p.freeKbaPerMonth > 0) freeSec.push(`${p.freeKbaPerMonth} free KBA`);
            if (p.smsPrice === 0) freeSec.push('SMS auth included');
            else if (p.freeSmsPerMonth > 0) freeSec.push(`${p.freeSmsPerMonth} free SMS`);
            if (freeSec.length > 0) {
                out.push(freeSec.join(' + ') + ' / mo');
            }

            // Overage - always useful to see
            if (p.packageLimit > 0) {
                out.push(`$${p.overagePerPackage.toFixed(2)} per-signing overage`);
            }

            return out;
        }
    },
    methods: {
        reloadPage() {
            if (typeof window !== 'undefined') {
                window.location.reload();
            }
        }
    }
};
</script>

<style scoped>
.plan-card {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.plan-card--compact {
    padding: 16px 14px;
}
.plan-card--error {
    border: 1px dashed #FCA5A5;
    background: #FEF2F2;
    text-align: center;
    justify-content: center;
    align-items: center;
    padding: 28px 20px;
}
</style>
